import { BehaviorSubject, firstValueFrom } from 'rxjs'
import type { User } from '@app/types/user.type'
import OktaAuth from '@okta/okta-auth-js'
import type { SsoConfigurationResponse } from '@backend-models/configuration/sso-configuration-response'
import { getMe, stopImpersonation, stopAllImpersonations } from '@app/services/user.service'
import type { Role } from '@backend-models/roles/role'
import type { Permission } from '@backend-models/permissions/permission'
import { dialog } from '@app/shared/dialog'

// okta memory
let oktaAuth: OktaAuth | null = null

// user subject which starts null by default until user login
const $user = new BehaviorSubject<User>(null)

// user observable for subscriptions
const $userObservable = $user.asObservable()

/**
 * Initialize Okta
 * @param settings
 */
export const initializeOkta = async (settings: SsoConfigurationResponse) => {
  oktaAuth = new OktaAuth({
    ...settings,
    tokenManager: {
      autoRenew: true,
    },
    redirectUri: location.origin,
  })
  if (oktaAuth.isLoginRedirect()) {
    try {
      await oktaAuth.handleLoginRedirect()
      // start okta manager
      oktaAuth?.start()
      localStorage.setItem('session-start-date', new Date().toISOString())
      localStorage.removeItem('absolute-session-seen')
      // Clean up any orphaned impersonation sessions from previous login
      await stopAllImpersonations()
    } catch (error) {
      handleOktaError(error)
      throw error
    }
  }
}

/**
 * Login using Okta
 * @returns boolean
 */
export const login = async () => {
  try {
    const authenticated = await oktaAuth?.isAuthenticated()
    if (!authenticated) {
      // set requested page
      localStorage.setItem('requested-page', `${location.pathname}${location.search}`)
      localStorage.removeItem('session-start-date')
      localStorage.removeItem('absolute-session-seen')
      sessionStorage.removeItem('impersonation-token')
      await oktaAuth?.signInWithRedirect()
      return false
    }

    // get user from api
    const { data, success } = await getMe()
    if (!success) {
      return false
    }
    setUser(data)
    return true
  } catch (error) {
    console.error(error)
    return false
  }
}

/**
 * Refresh user from system
 */
export const refreshUser = async () => {
  const { data, success } = await getMe()
  if (!success) {
    return false
  }
  setUser(data)
  return true
}

/**
 * Logout from Okta
 */
export const logout = async () => {
  const user = await getUser()

  // Always attempt to stop impersonation if user has active session
  if (user?.hasActiveImpersonationSession) {
    try {
      await stopImpersonation()
    } catch (error) {
      console.error('Failed to stop impersonation during logout:', error)
      // Continue with logout even if this fails
    }
  }

  // Clear impersonation token from sessionStorage (safety net)
  sessionStorage.removeItem('impersonation-token')

  removeUser()
  return oktaAuth?.signOut({ clearTokensBeforeRedirect: true })
}

/**
 * Try to refresh okta token
 * @returns boolean
 */
export const refreshTokenSilently = async () => {
  try {
    const renewToken = await oktaAuth?.token.renewTokens()
    if (renewToken) {
      oktaAuth?.tokenManager.setTokens(renewToken)
      return true
    } else {
      return false
    }
  } catch (error) {
    console.log(error)
    return false
  }
}

/**
 * Set current user (should be called on login or impersonation)
 * @param user
 */
export const setUser = (user: User) => $user.next(user)

/**
 * Remove current user (should be called on logout or end impersonation)
 */
export const removeUser = () => $user.next(null)

/**
 * Get user observable to listen for user changes
 * @returns Observable
 */
export const getUserObservable = () => $userObservable

/**
 * Get access token
 * @returns string
 */
export const getAccessToken = () => oktaAuth?.getAccessToken()

/**
 * Get current user
 * @returns Promise
 */
export const getUser = () => firstValueFrom(getUserObservable())

/**
 * Check if user has roles and optinal permissions
 * @param roles
 * @param permissions
 * @returns boolean
 */
export const hasUserRole = async (roles: Role[], permissions?: Permission[]) => {
  const user = await getUser()
  const hasRole = user?.roles.some((role) => roles.includes(role.value))
  const hasPermission = user?.permissions.some((permission) => permissions?.includes(permission.value))
  // if hasRole is true and no permission are provided or hasPermission is true then return true
  if (hasRole && (!permissions || hasPermission)) {
    return true
  }
  return false
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleOktaError = (error: any) => {
  const msg = error?.message?.toLowerCase() || ''
  const name = error?.name || ''
  const errorCode = error?.errorCode || error?.error?.errorCode

  let dialogMessage = ''

  if (msg.includes('issued in the future') || msg.includes('not yet valid')) {
    dialogMessage = `Your device clock is incorrect, preventing sign-in. Enable automatic date/time in your system settings, refresh, and retry. If this doesn't help, contact <a href="mailto:PMOffice@ati.org">PMOffice@ati.org</a>`
  } else if (msg.includes('parse a token from the url') || msg.includes('retrieve oauth redirect params')) {
    dialogMessage = `Sign-in was interrupted by a browser storage or cookie issue. Close this tab, open a new incognito/private window, and try again. If this doesn't help, contact <a href="mailto:PMOffice@ati.org">PMOffice@ati.org</a>`
  } else if (errorCode?.includes('E0000007') || errorCode?.includes('E0000005')) {
    dialogMessage = `You're authenticated with Okta, but we couldn't create your app session. Please re-open the application. If this doesn't help, contact <a href="mailto:PMOffice@ati.org">PMOffice@ati.org</a>`
  } else if (name === 'AuthSdkError' && msg.includes('invalid') && !msg.includes('grant')) {
    dialogMessage = `Your login token is no longer valid. Please sign in again.`
  } else if (msg.includes('access_denied') || msg.includes('insufficient_scope')) {
    dialogMessage = `We couldn't grant access due to permissions or configuration. If you believe this is an error, contact <a href="mailto:PMOffice@ati.org">PMOffice@ati.org</a>`
  } else if (msg.includes('pkce') || msg.includes('does not support')) {
    dialogMessage = `Your browser/environment doesn't fully support secure sign-in. Please upgrade your browser to the latest version or try a different browser`
  } else {
    dialogMessage = `Something went wrong during login. Please try again. If it persists, contact <a href="mailto:PMOffice@ati.org">PMOffice@ati.org</a>`
  }

  dialog({
    header: 'Login Error',
    message: dialogMessage,
  })
}
